import { Integration } from "../../Everou/_modelsTS/Integration";

/**
 * How to show a template to the manager.
 *
 * A template used to be identified by company + credentials only, which is
 * unreadable in a picker as soon as there are two accounts of the same PMS.
 * `name` (optional, entered by the manager) is the readable one; without it we
 * fall back to whatever identifies the remote account -- the hotel id or the
 * login -- and, failing those, to the tail of the apikey, which at least tells
 * two accounts apart.
 */
// Icnea, Bookipro and Three Angular store this literal as their apikey: it is
// a placeholder for "the PMS pushes to us", not a credential, so it identifies
// nothing and every account of theirs would look the same.
const PLACEHOLDER_APIKEY = "granted";

function accountKey(template: Integration, field: string) {
    const value = template[field];
    if (!value) return "";
    if (field === "apikey" && value === PLACEHOLDER_APIKEY) return "";
    return String(value);
}

export function integrationTemplateLabel(template?: Integration | null): string {
    if (!template) return "";
    if (template.name) return template.name;

    const apikey = accountKey(template, "apikey");
    const account =
        template.hotel_id ||
        template.username ||
        (apikey ? `…${apikey.slice(-4)}` : "");

    return account ? `${template.company} · ${account}` : String(template.company);
}

/**
 * Whether two templates hold credentials of DIFFERENT remote accounts.
 *
 * Used to warn before a move: the integration keeps its property (host_id /
 * room_id), which is an id of the old account, so moving it to another account
 * only works if the same property exists there -- and only the manager knows.
 * Unknown (a missing value on either side) counts as "same": a warning shown
 * without reason is worse than one not shown.
 */
export function isDifferentAccount(from?: Integration | null, to?: Integration | null): boolean {
    if (!from || !to) return false;

    return ["apikey", "hotel_id", "username"].some(field => {
        const a = accountKey(from, field);
        const b = accountKey(to, field);
        return !!a && !!b && a !== b;
    });
}
